import { useEffect } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fetchJson } from '@atlassian/jira-fetch/src/index.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueNavigatorIssueSearchRefetchQuery$variables } from '../../__generated__/IssueNavigatorIssueSearchRefetchQuery.graphql';
import type {
	JiraIssueSearchInput,
	main_IssueNavigatorQuery$variables,
} from '../../__generated__/main_IssueNavigatorQuery.graphql';
import {
	NIN_GLOBAL_SHADOW_REQUEST_OPERATION_SCOPE,
	NIN_PROJECT_SHADOW_REQUEST_OPERATION_SCOPE,
} from '../../common/constants.tsx';

type ShadowRequestVariables =
	| {
			cloudId: string;
			issueSearchInput: JiraIssueSearchInput;
			viewId: string;
	  }
	| IssueNavigatorIssueSearchRefetchQuery$variables
	| main_IssueNavigatorQuery$variables;

export const fetchShadowRequest = async (
	variables: ShadowRequestVariables,
	query: string,
	operationName: string,
) => {
	try {
		await fetchJson('/gateway/api/graphql', {
			method: 'POST',
			headers: {
				'X-ExperimentalApi': 'JiraIssueSearch, JiraProject',
			},
			body: JSON.stringify({
				operationName,
				query,
				variables: {
					...variables,
					namespace: 'ISSUE_NAVIGATOR',
					amountOfColumns: 500,
					scope: {
						operationScope:
							'projectKey' in variables && variables.projectKey
								? NIN_PROJECT_SHADOW_REQUEST_OPERATION_SCOPE
								: NIN_GLOBAL_SHADOW_REQUEST_OPERATION_SCOPE,
					},
					isLinkRelationshipFieldEnabled: false,
					isLastUsedJqlEnabled: fg('jira_nin_last_used_jql'),
					__relay_internal__pv__atlassianjirarelayprovidersrcisbulkopsinninenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcisjscinfinitescrollenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissuelinkscountrelayprovider: 1,
					__relay_internal__pv__atlassianjirarelayprovidersrcissuelinksentrypointrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcnikechinstorypointsinlineeditrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissinglelinerowheightenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjiralistinlineeditingrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcthorcolorfulsingleselectmilestone2experimentrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjiralistreparentingrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjscissuerankingrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcninmultigrouppickercellrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcisjiraitrm4exp3enabledrelayprovider:
						expVal('jira-itr-m4-exp-3', 'isEnabled', false),
					__relay_internal__pv__atlassianjirarelayprovidersrcjscmergedcolumnrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcisrenderjirawatchesfieldninrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissavedlistviewenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissavedlistviewtechnicalchangesenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissavedlistviewconditionalformattingenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcisfrozencolumnsenabledrelayprovidertsx: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissavedlistviewtechnicalchangesdogfoodingenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissavedlistviewtechnicalchangesdogfoodingormergesupportenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcisjscifcenabledrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjiraaiagentavatarsingleuserpickerrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjiranumberfieldconfigurationenabledrelayprovider:
						fg('frontier-currency-field-d1'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjscaliaschangesrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcjiv20953querybystatusfielddirectlyrelayprovider: true,
					__relay_internal__pv__atlassianjirarelayprovidersrcissueviewdforemovefieldsbyidrelayprovider:
						fg('dfo_remove_fields_by_id'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiratimelineanalyticsharmonizationrelayprovider:
						fg('jira_timeline_analytics_harmonization'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiraconfluencepagecontentenabledrelayprovider:
						fg('jira_preview_panel_m_1_rollout'),
					__relay_internal__pv__atlassianjirarelayprovidersrcinitialisevirtualiserstatefromrelaystorerelayprovider:
						fg('initialise_virtualiser_state_from_relay_store'),
					__relay_internal__pv__atlassianjirarelayprovidersrcmimicflatlistrsswhileuninitialisedrelayprovider:
						fg('mimic_flatlist_rss_while_uninitialised'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiralistreducererendersrelayprovider:
						fg('jira_list_reduce_re_renders'),
					__relay_internal__pv__atlassianjirarelayprovidersrcbulkopsselectallrelayprovider: expVal(
						'bulk_ops_select_all_beyond_experiment',
						'isEnabled',
						false,
					),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiraninlastusedjqlrelayprovider:
						fg('jira_nin_last_used_jql'),
					__relay_internal__pv__atlassianjirarelayprovidersrcatlasprojectnativeintegrationrelayprovider:
						expValEquals(
							'atlassian_projects_-_native_integration',
							'cohort',
							'variation',
							'control',
						),
					__relay_internal__pv__atlassianjirarelayprovidersrcninresolutionstrikethoughenabledrelayprovider:
						fg('nin-strikethrough-resolution-update'),
					__relay_internal__pv__atlassianjirarelayprovidersrcgroupbyprojectsrelayprovider:
						fg('group_by_projects'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiraaishowhumansupervisoravatarrelayprovider:
						fg('jira_ai_show_human_supervisor_avatar'),
					__relay_internal__pv__atlassianjirarelayprovidersrcisjirapaidlistninmergeonboardingmodalenabledrelayprovider:
						fg('jira_paid_list_nin_merge_onboarding_modal'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiraoriginalestimateeditablefixrelayprovider:
						fg('jira-original-estimate-editable-fix'),
					__relay_internal__pv__atlassianjirarelayprovidersrcfrontierformulafieldd1relayprovider:
						fg('frontier-formula-field-d1'),
					__relay_internal__pv__atlassianjirarelayprovidersrcisiframepreviewpanelinsubtasksenabledrelayprovider:
						fg('issue_preview_panel_issue_view_subtask'),
					__relay_internal__pv__atlassianjirarelayprovidersrcgoalsssrsmartcardrelayprovider:
						fg('goals_ssr_smart_card'),
					__relay_internal__pv__atlassianjirarelayprovidersrcjiratimelinerollupstechnicalgaterelayprovider:
						fg('jira_timeline_rollups_technical_gate'),
				},
			}),
		});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			attributes: { message: `Shadow request for nin issue search ${operationName} failed` },
			meta: {
				id: 'shadow-request-for-nin-issue-search',
				packageName: 'jiraIssueNavigator',
				teamName: 'empanada',
			},
		});
	}
};

const getIssueNavigatorIssueSearchRefetchQueryShadowOperation = () => {
	const operation = import(
		/* webpackChunkName: "async-shadow-issue-search-refetch-query" */ '@atlassian/jira-relay/./src/common/mocks/IssueNavigatorIssueSearchRefetchQuery.json'
	).then((module) => {
		const operationName = 'SLV_SHADOW_REQUEST_IssueNavigatorIssueSearchRefetchQuery';
		return {
			operationName,
			operation: module.operation.replace('IssueNavigatorIssueSearchRefetchQuery', operationName),
		};
	});
	return operation;
};

const getMainIssueNavigatorQueryShadowOperation = () => {
	const operation = import(
		/* webpackChunkName: "async-shadow-main-issue-navigator-query" */ '@atlassian/jira-relay/./src/common/mocks/main_IssueNavigatorQuery.json'
	).then((module) => {
		const operationName = 'SLV_SHADOW_REQUEST_main_IssueNavigatorQuery';
		return {
			operationName,
			operation: module.operation.replace('main_IssueNavigatorQuery', operationName),
		};
	});
	return operation;
};

const catchedCallback = <T extends Array<A>, A>(cb: (...args: T) => void) => {
	return (...args: T) => {
		try {
			cb(...args);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				attributes: { message: 'Queue shadow request for nin issue search failed' },
				meta: {
					id: 'queue-shadow-request-for-nin-issue-search',
					packageName: 'jiraIssueNavigator',
					teamName: 'empanada',
				},
			});
		}
	};
};

const catchedCallbackAsync = <T extends Array<A>, A>(cb: (...args: T) => Promise<void>) => {
	return async (...args: T) => {
		try {
			await cb(...args);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				attributes: { message: 'Queue shadow request for nin issue search failed' },
				meta: {
					id: 'queue-shadow-request-for-nin-issue-search',
					packageName: 'jiraIssueNavigator',
					teamName: 'empanada',
				},
			});
		}
	};
};

export const queueShadowRequest = catchedCallback(
	(
		getOperation: () => Promise<{
			operationName: string;
			operation: string;
		}>,
		variables: ShadowRequestVariables,
	) => {
		if (__SERVER__) return;

		// requestIdleCallback is not supported in Safari, so we need a workaround.
		if (typeof window !== 'undefined' && typeof window.requestIdleCallback !== 'undefined') {
			window.requestIdleCallback(
				catchedCallback(() =>
					setTimeout(
						catchedCallback(() => {
							window.requestIdleCallback(
								catchedCallbackAsync(async () => {
									const query = await getOperation();
									fetchShadowRequest(variables, query.operation, query.operationName);
								}),
							);
						}),
						5000,
					),
				),
			);
		}
	},
);

export const fetchIssueNavigatorIssueSearchRefetchQueryShadow = (
	variables: IssueNavigatorIssueSearchRefetchQuery$variables,
) => {
	queueShadowRequest(getIssueNavigatorIssueSearchRefetchQueryShadowOperation, variables);
};

export const useMainIssueNavigatorQueryShadowRequest = (
	variables: main_IssueNavigatorQuery$variables,
) => {
	useEffect(() => {
		if (fg('saved_list_view_shadow_request')) {
			queueShadowRequest(getMainIssueNavigatorQueryShadowOperation, variables);
		}
		// We only want to fetch the main query once, so we don't need to add any dependencies here

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
